import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react'
import { useAuthContext } from '../hooks/useAuthContext'
import {
  acknowledgeNotification as acknowledgeNotificationRequest,
  getNotifications,
  ListNotificationsParams,
  NotificationItem,
  resolveNotification as resolveNotificationRequest,
} from '../helper/notifcationsHelper'

type NotificationState = {
  notifications: NotificationItem[]
  listTotal: number
  loading: boolean
  error: string | null
  activeAlertTotal: number
}

type NotificationAction =
  | { type: 'SET_LOADING' }
  | { type: 'SET_NOTIFICATIONS'; payload: { items: NotificationItem[]; total: number } }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'UPSERT_NOTIFICATION'; payload: NotificationItem }
  | { type: 'SET_ACTIVE_BADGE'; payload: number }

type NotificationContextType = NotificationState & {
  refreshNotifications: (override?: Partial<ListNotificationsParams>) => Promise<void>
  refreshActiveBadge: () => Promise<void>
  acknowledgeById: (id: string) => Promise<boolean>
  resolveById: (id: string) => Promise<boolean>
  /** Alias for navbar: count of active alerts from API (not limited by list page size). */
  activeCount: number
}

const defaultListParams: ListNotificationsParams = { limit: 100, page: 1 }

export const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  listTotal: 0,
  loading: false,
  error: null,
  activeAlertTotal: 0,
  refreshNotifications: async () => {},
  refreshActiveBadge: async () => {},
  acknowledgeById: async () => false,
  resolveById: async () => false,
  activeCount: 0,
})

const notificationReducer = (state: NotificationState, action: NotificationAction): NotificationState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: true, error: null }
    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        loading: false,
        notifications: action.payload.items,
        listTotal: action.payload.total,
        error: null,
      }
    case 'SET_ERROR':
      return { ...state, loading: false, error: action.payload }
    case 'SET_ACTIVE_BADGE':
      return { ...state, activeAlertTotal: action.payload }
    case 'UPSERT_NOTIFICATION': {
      const idx = state.notifications.findIndex((n) => n._id === action.payload._id)
      if (idx === -1) {
        return { ...state, notifications: [action.payload, ...state.notifications] }
      }
      const next = [...state.notifications]
      next[idx] = action.payload
      return { ...state, notifications: next }
    }
    default:
      return state
  }
}

type NotificationProviderProps = {
  children: React.ReactNode
}

export const NotificationContextProvider = ({ children }: NotificationProviderProps) => {
  const [state, dispatch] = useReducer(notificationReducer, {
    notifications: [],
    listTotal: 0,
    loading: false,
    error: null,
    activeAlertTotal: 0,
  })
  const { user } = useAuthContext()
  const listParamsRef = useRef<ListNotificationsParams>({ ...defaultListParams })

  const refreshActiveBadge = useCallback(async () => {
    if (!user) {
      dispatch({ type: 'SET_ACTIVE_BADGE', payload: 0 })
      return
    }
    const response = await getNotifications(user, { status: 'active', limit: 1, page: 1 })
    if ('error' in response) return
    dispatch({ type: 'SET_ACTIVE_BADGE', payload: response.total })
  }, [user])

  const refreshNotifications = useCallback(
    async (override?: Partial<ListNotificationsParams>) => {
      if (!user) {
        dispatch({ type: 'SET_NOTIFICATIONS', payload: { items: [], total: 0 } })
        return
      }
      const next: ListNotificationsParams =
        override !== undefined
          ? { ...defaultListParams, ...override }
          : { ...defaultListParams, ...listParamsRef.current }
      listParamsRef.current = next
      dispatch({ type: 'SET_LOADING' })
      const response = await getNotifications(user, next)
      if ('error' in response) {
        console.error('Failed to load notifications:', response.error)
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to load notifications. Check that the API server is running.',
        })
        return
      }
      dispatch({ type: 'SET_NOTIFICATIONS', payload: { items: response.items, total: response.total } })
      void refreshActiveBadge()
    },
    [user, refreshActiveBadge]
  )

  const acknowledgeById = useCallback(
    async (id: string) => {
      if (!user) return false
      const response = await acknowledgeNotificationRequest(user, id)
      if ('error' in response) {
        console.error('Failed to acknowledge notification:', response.error)
        dispatch({ type: 'SET_ERROR', payload: 'Failed to acknowledge notification.' })
        return false
      }
      dispatch({ type: 'UPSERT_NOTIFICATION', payload: response })
      void refreshActiveBadge()
      return true
    },
    [user, refreshActiveBadge]
  )

  const resolveById = useCallback(
    async (id: string) => {
      if (!user) return false
      const response = await resolveNotificationRequest(user, id)
      if ('error' in response) {
        console.error('Failed to resolve notification:', response.error)
        dispatch({ type: 'SET_ERROR', payload: 'Failed to resolve notification.' })
        return false
      }
      dispatch({ type: 'UPSERT_NOTIFICATION', payload: response })
      void refreshActiveBadge()
      return true
    },
    [user, refreshActiveBadge]
  )

  useEffect(() => {
    listParamsRef.current = { ...defaultListParams }
    if (!user) {
      dispatch({ type: 'SET_NOTIFICATIONS', payload: { items: [], total: 0 } })
      dispatch({ type: 'SET_ACTIVE_BADGE', payload: 0 })
      return
    }
    void refreshActiveBadge()
  }, [user, refreshActiveBadge])

  const activeCount = useMemo(() => state.activeAlertTotal, [state.activeAlertTotal])

  return (
    <NotificationContext.Provider
      value={{
        ...state,
        refreshNotifications,
        refreshActiveBadge,
        acknowledgeById,
        resolveById,
        activeCount,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}
